import { FC, useCallback, useMemo, useState } from 'react';

import { XIcon } from '@heroicons/react/outline';
import {
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
  GiftCouponSummary,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { cancelGiftOrder, partialCancelGiftOrder } from '@shared/api/giftOrder/giftOrder.controller';
import {
  GiftOrderForAdmin,
  GiftOrderItemType,
  GiftOrderPaymentStatus,
} from '@shared/api/giftOrder/giftOrder.interface';
import { LowerRole } from '@shared/types';

const redeemStatusLabel: Record<GiftCouponRedeemStatus, string> = {
  [GiftCouponRedeemStatus.Available]: '사용가능',
  [GiftCouponRedeemStatus.Requested]: '교환신청',
  [GiftCouponRedeemStatus.Delivered]: '발송완료',
  [GiftCouponRedeemStatus.Rejected]: '발송불가',
  [GiftCouponRedeemStatus.Expired]: '만료',
  [GiftCouponRedeemStatus.Refunded]: '환불',
  [GiftCouponRedeemStatus.Canceled]: '취소',
};

const redeemStatusColor: Record<GiftCouponRedeemStatus, string> = {
  [GiftCouponRedeemStatus.Available]: 'bg-blue-100 text-blue-800',
  [GiftCouponRedeemStatus.Requested]: 'bg-yellow-100 text-yellow-800',
  [GiftCouponRedeemStatus.Delivered]: 'bg-green-100 text-green-800',
  [GiftCouponRedeemStatus.Rejected]: 'bg-orange-100 text-orange-800',
  [GiftCouponRedeemStatus.Expired]: 'bg-gray-100 text-gray-500',
  [GiftCouponRedeemStatus.Refunded]: 'bg-red-100 text-red-800',
  [GiftCouponRedeemStatus.Canceled]: 'bg-gray-100 text-gray-500',
};

const shareStatusLabel: Record<GiftCouponShareStatus, string> = {
  [GiftCouponShareStatus.NotShared]: '미공유',
  [GiftCouponShareStatus.Shared]: '공유',
  [GiftCouponShareStatus.Viewed]: '열람',
};

const shareStatusColor: Record<GiftCouponShareStatus, string> = {
  [GiftCouponShareStatus.NotShared]: 'bg-gray-100 text-gray-500',
  [GiftCouponShareStatus.Shared]: 'bg-indigo-100 text-indigo-700',
  [GiftCouponShareStatus.Viewed]: 'bg-purple-100 text-purple-700',
};

const fmt = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
};

const fmtTime = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}.${m}.${day} ${h}:${min}`;
};

const fmtPhone = (phone: string | undefined) => {
  if (!phone) return '';
  const n = phone.replace(/[^0-9]/g, '');
  if (n.length === 11) return `${n.slice(0, 3)}-${n.slice(3, 7)}-${n.slice(7)}`;
  return phone;
};

const isCouponCancellable = (c: GiftCouponSummary): boolean =>
  c.shareStatus === GiftCouponShareStatus.NotShared &&
  c.redeemStatus === GiftCouponRedeemStatus.Available;

interface CouponDetailModalProps {
  coupons: GiftCouponSummary[];
  orderId?: string;
  order?: GiftOrderForAdmin;
  onClose: () => void;
}

export const CouponDetailModal: FC<CouponDetailModalProps> = ({
  coupons,
  orderId,
  order,
  onClose,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelResult, setCancelResult] = useState<string | null>(null);

  const sortedCoupons = useMemo(
    () => [...coupons].sort((a, b) => a.seq - b.seq),
    [coupons],
  );

  const stats = useMemo(() => {
    const total = coupons.length;
    const notShared = coupons.filter(
      (c) => c.shareStatus === GiftCouponShareStatus.NotShared,
    ).length;
    const available = coupons.filter(
      (c) =>
        c.redeemStatus !== GiftCouponRedeemStatus.Delivered &&
        c.redeemStatus !== GiftCouponRedeemStatus.Refunded &&
        c.redeemStatus !== GiftCouponRedeemStatus.Canceled,
    ).length;
    const delivered = coupons.filter(
      (c) => c.redeemStatus === GiftCouponRedeemStatus.Delivered,
    ).length;
    return { total, notShared, available, delivered };
  }, [coupons]);

  const cancellableCoupons = useMemo(
    () => sortedCoupons.filter(isCouponCancellable),
    [sortedCoupons],
  );

  const canCancel = useMemo(() => {
    if (!orderId || !order) return false;
    const status = order.paymentDetail.status;
    return (
      status === GiftOrderPaymentStatus.Paid ||
      status === GiftOrderPaymentStatus.Confirmed
    );
  }, [orderId, order]);

  const hasDeliveryItems = useMemo(() => {
    if (!order) return false;
    return order.orderDetails.some((d) => d.type === GiftOrderItemType.Delivery);
  }, [order]);

  const allNotSharedDeliveries = useMemo(() => {
    if (!order || !order.deliveries) return true;
    const deliveries = order.deliveries as { shareStatus: string; deliveryStatus?: string }[];
    return deliveries.every((d) => d.shareStatus === 'NotShared');
  }, [order]);

  const isFullCancel = useMemo(() => {
    if (!canCancel) return false;
    const activeCoupons = coupons.filter(
      (c) => c.redeemStatus !== GiftCouponRedeemStatus.Canceled,
    );
    const allCouponsSelected =
      activeCoupons.length > 0 && activeCoupons.every((c) => selectedIds.has(c._id));
    if (!allCouponsSelected) return false;
    if (hasDeliveryItems) return allNotSharedDeliveries;
    return true;
  }, [canCancel, coupons, selectedIds, hasDeliveryItems, allNotSharedDeliveries]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === cancellableCoupons.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(cancellableCoupons.map((c) => c._id)));
    }
  }, [cancellableCoupons, selectedIds.size]);

  const handleCancelRequest = useCallback(async () => {
    if (!orderId || selectedIds.size === 0 || !cancelReason.trim()) return;

    setIsCancelling(true);
    setCancelResult(null);

    try {
      if (isFullCancel) {
        await cancelGiftOrder(
          { _id: orderId, cancelReason: cancelReason.trim() },
          LowerRole.Admin,
        );
        setCancelResult('전체취소 요청이 완료되었습니다.');
      } else {
        const result = await partialCancelGiftOrder({
          _id: orderId,
          cancelReason: cancelReason.trim(),
          couponIds: Array.from(selectedIds),
          deliveryIds: [],
        });
        setCancelResult(
          `부분취소 완료: ${result.data.cancelledPrice.toLocaleString()}원 환불`,
        );
      }
      setShowCancelConfirm(false);
      setSelectedIds(new Set());
      setCancelReason('');
      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 1500);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : '취소 처리 중 오류가 발생했습니다.';
      setCancelResult(`오류: ${message}`);
    } finally {
      setIsCancelling(false);
    }
  }, [orderId, selectedIds, cancelReason, isFullCancel, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-bold text-gray-900">쿠폰 상세내역</h2>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>
                전체 <span className="font-semibold text-gray-700">{stats.total}</span>
              </span>
              <span className="text-gray-300">|</span>
              <span>
                미공유{' '}
                <span
                  className={`font-semibold ${
                    stats.notShared > 0 ? 'text-orange-600' : 'text-gray-400'
                  }`}
                >
                  {stats.notShared}
                </span>
              </span>
              <span className="text-gray-300">|</span>
              <span>
                미사용{' '}
                <span
                  className={`font-semibold ${
                    stats.available > 0 ? 'text-blue-600' : 'text-gray-400'
                  }`}
                >
                  {stats.available}
                </span>
              </span>
              <span className="text-gray-300">|</span>
              <span>
                교환{' '}
                <span
                  className={`font-semibold ${
                    stats.delivered > 0 ? 'text-green-600' : 'text-gray-400'
                  }`}
                >
                  {stats.delivered}
                </span>
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Cancel result banner */}
        {cancelResult && (
          <div
            className={`mx-4 mb-2 rounded-lg px-4 py-2.5 text-sm font-medium ${
              cancelResult.startsWith('오류')
                ? 'bg-red-50 text-red-700'
                : 'bg-green-50 text-green-700'
            }`}
          >
            {cancelResult}
          </div>
        )}

        <div className="px-4 pb-4">
          {/* Cancel action bar */}
          {canCancel && cancellableCoupons.length > 0 && (
            <div className="mb-3 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-2.5">
              <div className="flex items-center gap-3">
                <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={
                      selectedIds.size === cancellableCoupons.length &&
                      cancellableCoupons.length > 0
                    }
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  취소 가능 전체선택 ({cancellableCoupons.length}건)
                </label>
                {selectedIds.size > 0 && (
                  <span className="text-xs text-red-600">
                    {selectedIds.size}건 선택됨
                    {isFullCancel ? ' (전체취소)' : ' (부분취소)'}
                  </span>
                )}
              </div>
              {selectedIds.size > 0 && (
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  className="rounded-lg bg-red-600 px-4 py-1.5 text-sm font-bold text-white hover:bg-red-700"
                >
                  선택 쿠폰 취소 ({selectedIds.size}건)
                </button>
              )}
            </div>
          )}

          {/* Table */}
          <div
            className="overflow-auto rounded-md border"
            style={{ maxHeight: 'calc(90vh - 140px)' }}
          >
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50">
                <tr className="text-left text-gray-500">
                  {canCancel && cancellableCoupons.length > 0 && (
                    <th className="whitespace-nowrap px-3 py-2 font-medium">선택</th>
                  )}
                  <th className="whitespace-nowrap px-3 py-2 font-medium">#</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">코드</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">교환상태</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">공유</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">구매일</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">만료/교환일</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">공유일</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">열람일</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">신청자</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">신청일시</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">환불</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedCoupons.map((c) => {
                  const cancellable = isCouponCancellable(c);
                  const isSelected = selectedIds.has(c._id);
                  const isCanceled = c.redeemStatus === GiftCouponRedeemStatus.Canceled;

                  return (
                    <tr
                      key={c._id}
                      className={`${
                        isCanceled
                          ? 'bg-gray-50 opacity-60'
                          : isSelected
                          ? 'bg-red-50'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      {canCancel && cancellableCoupons.length > 0 && (
                        <td className="whitespace-nowrap px-3 py-1.5">
                          {cancellable ? (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelect(c._id)}
                              className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                            />
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      )}
                      <td className="whitespace-nowrap px-3 py-1.5 text-gray-400">
                        {c.seq}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 font-mono font-medium text-gray-800">
                        {c.code}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5">
                        <span
                          className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${
                            redeemStatusColor[c.redeemStatus]
                          }`}
                        >
                          {redeemStatusLabel[c.redeemStatus]}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5">
                        <span
                          className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${
                            shareStatusColor[c.shareStatus]
                          }`}
                        >
                          {shareStatusLabel[c.shareStatus]}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
                        {fmt(c.purchasedAt)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5">
                        {c.redeemStatus === GiftCouponRedeemStatus.Delivered &&
                        c.gifticon?.deliveredAt ? (
                          <span className="font-medium text-green-600">
                            {fmt(c.gifticon.deliveredAt)}
                          </span>
                        ) : (
                          <span className="text-gray-500">{fmt(c.expiresAt)}</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
                        {c.sharedAt ? fmt(c.sharedAt) : '-'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
                        {c.viewedAt ? fmt(c.viewedAt) : '-'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-gray-700">
                        {c.recipient ? (
                          <span>
                            {c.recipient.name}{' '}
                            <span className="text-gray-400">
                              {fmtPhone(c.recipient.phoneNumber)}
                            </span>
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
                        {c.recipient ? fmtTime(c.recipient.requestedAt) : '-'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5">
                        {c.refund ? (
                          <span className="text-red-600">
                            {c.refund.amount.toLocaleString()}원{' '}
                            <span className="text-red-400">
                              {fmt(c.refund.refundedAt)}
                            </span>
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Cancel confirmation dialog */}
        {showCancelConfirm && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/40"
              onClick={() => !isCancelling && setShowCancelConfirm(false)}
            />
            <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
              <h3 className="text-lg font-bold text-gray-900">
                {isFullCancel ? '전체취소 확인' : '부분취소 확인'}
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                {isFullCancel
                  ? `주문 전체를 취소합니다. 전액 환불됩니다.`
                  : `선택한 쿠폰 ${selectedIds.size}건을 취소합니다. 해당 금액만 환불됩니다.`}
              </p>

              {isFullCancel && hasDeliveryItems && (
                <div className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  배송 상품도 함께 전체취소됩니다.
                </div>
              )}

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">
                  취소 사유 <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="취소 사유를 입력하세요"
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-red-400 focus:outline-none focus:ring-1 focus:ring-red-400"
                  rows={3}
                />
              </div>

              <div className="mt-5 flex gap-3">
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  disabled={isCancelling}
                  className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                >
                  닫기
                </button>
                <button
                  onClick={handleCancelRequest}
                  disabled={isCancelling || !cancelReason.trim()}
                  className="flex-1 rounded-lg bg-red-600 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {isCancelling
                    ? '처리중...'
                    : isFullCancel
                    ? '전체취소 실행'
                    : '부분취소 실행'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
